/**
 * Centralized Error Management Service
 * Single source of truth for all error handling across the application
 * Polivalent, solid, and robust error processing and user feedback
 */

import { useNotifications } from '@/shared/components/Notification'
import { ApiException } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

// Type for API error details from backend response
interface ApiErrorDetails {
  message?: string
  code?: string
  error?: {
    message?: string
    code?: string
  }
  [key: string]: unknown
}

function isApiErrorDetails(value: unknown): value is ApiErrorDetails {
  return value !== null && typeof value === 'object'
}

// Unified Error Types
interface ProcessedError {
  message: string
  code: string
  field?: string
  severity: 'error' | 'warning' | 'info'
  shouldNotify: boolean
  shouldLog: boolean
  context?: Record<string, unknown>
}

// Error Categories
enum ErrorCategory {
  AUTHENTICATION = 'auth',
  VALIDATION = 'validation',
  NETWORK = 'network',
  PERMISSION = 'permission',
  BUSINESS_LOGIC = 'business',
  SYSTEM = 'system',
  USER_INPUT = 'input',
}

// REMOVED: No more frontend error message interpretation
// Backend is the single source of truth for error copy. The service simply
// surfaces the original message without regex-based re-classification.

/**
 * Universal Error Processing Engine
 * Handles all error types with consistent processing
 */
class ErrorService {
  private static instance: ErrorService
  private notificationHook?: ReturnType<typeof useNotifications>

  private constructor() {}

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService()
    }
    return ErrorService.instance
  }

  /**
   * Initialize with notification hook for user feedback
   */
  initialize(notificationHook: ReturnType<typeof useNotifications>) {
    this.notificationHook = notificationHook
  }

  /**
   * Universal error processing - handles any error type
   */
  processError(error: unknown, context?: Record<string, unknown>): ProcessedError {
    // Handle ApiException (backend errors)
    if (error instanceof ApiException) {
      return this.processApiError(error, context)
    }

    // Handle generic Error
    if (error instanceof Error) {
      return this.processGenericError(error, context)
    }

    // Handle string errors
    if (typeof error === 'string') {
      return this.processStringError(error, context)
    }

    // Handle unknown error types
    return this.processUnknownError(error, context)
  }

  /**
   * Error processing with i18n support for React components
   * Accepts a translation function to enable localization
   */
  processErrorWithTranslation(
    error: unknown,
    translateFn: (key: string, defaultValue?: string) => string,
    context?: Record<string, unknown>,
  ): ProcessedError {
    // Handle ApiException (backend errors) with translation
    if (error instanceof ApiException) {
      return this.processApiErrorWithTranslation(error, translateFn, context)
    }

    // Handle generic Error with translation
    if (error instanceof Error) {
      return this.processGenericErrorWithTranslation(error, translateFn, context)
    }

    // Handle string errors with translation
    if (typeof error === 'string') {
      return this.processStringErrorWithTranslation(error, translateFn, context)
    }

    // Handle unknown error types with translation
    return this.processUnknownErrorWithTranslation(error, translateFn, context)
  }

  /**
   * Process API errors from backend
   */
  private processApiError(error: ApiException, context?: Record<string, unknown>): ProcessedError {
    // Mid-flight aborts surface as ApiException with code TIMEOUT /
    // NETWORK_ERROR / REQUEST_ABORTED but carry NO useful message —
    // they hit the last-resort fallback below and trigger a red toast
    // ("API error occurred") + console.error on every route change.
    // Demote to silent: the request was cancelled because the user
    // navigated away, there is nothing for them to act on.
    //
    // STRIPE_NOT_CONFIGURED is an environment/config gap, not a
    // user-actionable error: every /admin/org/billing visit in dev /
    // staging without Stripe credentials fired GETs for /billing/address
    // and /billing/payment-methods that returned 400 and spammed
    // console.error. Treat it like the abort codes — the billing page
    // already renders sensible empty states.
    const benignAbortCodes = new Set([
      'TIMEOUT',
      'NETWORK_ERROR',
      'REQUEST_ABORTED',
      'STRIPE_NOT_CONFIGURED',
    ])
    const detailCode = isApiErrorDetails(error.details) ? error.details.code : undefined
    if (
      (error.code && benignAbortCodes.has(error.code)) ||
      (detailCode && benignAbortCodes.has(detailCode))
    ) {
      return {
        message: '',
        code: error.code ?? detailCode ?? 'BENIGN',
        severity: 'info',
        shouldNotify: false,
        shouldLog: false,
        context: { ...context, status: error.status, benign: true },
      }
    }
    const details = isApiErrorDetails(error.details) ? error.details : undefined

    // Primary: Extract from error.details.message (backend response structure)
    if (details?.message) {
      return {
        message: this.getLocalizedMessage(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
          details.code || error.code || 'API_ERROR',
          details.message,
        ),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: details.code || error.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status, details },
      }
    }

    // Secondary: Extract from nested error.details.error structure (alternative backend structure)
    const backendError = details?.error
    if (backendError?.message) {
      return {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        message: this.getLocalizedMessage(backendError.code || 'API_ERROR', backendError.message),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: backendError.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status, backendError },
      }
    }

    // Fallback: Use error.message from the ApiException itself
    if (error.message && error.message !== 'API error occurred') {
      return {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        message: this.getLocalizedMessage(error.code || 'API_ERROR', error.message),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: error.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status },
      }
    }

    // Last resort fallback
    return {
      message: 'API error occurred',
      code: 'API_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context,
    }
  }

  /**
   * Process API errors from backend with translation support
   */
  private processApiErrorWithTranslation(
    error: ApiException,
    translateFn: (key: string, defaultValue?: string) => string,
    context?: Record<string, unknown>,
  ): ProcessedError {
    const details = isApiErrorDetails(error.details) ? error.details : undefined

    // Primary: Extract from error.details.message (backend response structure)
    if (details?.message) {
      return {
        message: this.getLocalizedMessage(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
          details.code || error.code || 'API_ERROR',
          details.message,
          translateFn,
        ),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: details.code || error.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status, details },
      }
    }

    // Secondary: Extract from nested error.details.error structure (alternative backend structure)
    const backendError = details?.error
    if (backendError?.message) {
      return {
        message: this.getLocalizedMessage(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
          backendError.code || 'API_ERROR',
          backendError.message,
          translateFn,
        ),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: backendError.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status, backendError },
      }
    }

    // Fallback: Use error.message from the ApiException itself
    if (error.message && error.message !== 'API error occurred') {
      return {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        message: this.getLocalizedMessage(error.code || 'API_ERROR', error.message, translateFn),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: error.code || 'API_ERROR',
        severity: this.getSeverityFromStatus(error.status),
        shouldNotify: true,
        shouldLog: true,
        context: { ...context, status: error.status },
      }
    }

    // Last resort fallback.
    // `translateFn` es el `t` que `useErrorHandler()` ata al espacio `validation`
    // (ver más abajo), así que esta clave vive en OTRO espacio y el salto se
    // escribe con DOS PUNTOS. Con punto (`errors.api_error`) i18next lo leería
    // como un subárbol `errors` DENTRO de validation.json —que no existe— y
    // caería siempre al `defaultValue` inglés, en cualquier idioma.
    // El espacio `errors` está en `NAMESPACES` (platform/i18n/config.ts), o sea
    // que i18next lo tiene cargado y el salto resuelve.
    return {
      message: translateFn('errors:api_error', 'API error occurred'),
      code: 'API_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context,
    }
  }

  /**
   * Process generic JavaScript errors
   * Pass-through: surface the original message without reinterpretation.
   */
  private processGenericError(error: Error, context?: Record<string, unknown>): ProcessedError {
    return {
      message: error.message,
      code: 'GENERIC_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, originalMessage: error.message, stack: error.stack },
    }
  }

  /**
   * Process string errors
   * Pass-through: surface the original string without reinterpretation.
   */
  private processStringError(error: string, context?: Record<string, unknown>): ProcessedError {
    return {
      message: error,
      code: 'STRING_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, originalMessage: error },
    }
  }

  /**
   * Process unknown error types
   */
  private processUnknownError(error: unknown, context?: Record<string, unknown>): ProcessedError {
    return {
      message: this.getLocalizedMessage('UNKNOWN_ERROR', 'An unexpected error occurred'),
      code: 'UNKNOWN_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, error: String(error) },
    }
  }

  /**
   * Process generic JavaScript errors with translation support
   * Pass-through: `translateFn` is accepted for signature symmetry with the
   * other *WithTranslation variants, but the backend message is surfaced
   * unchanged — no i18n key is applied at this layer.
   */
  private processGenericErrorWithTranslation(
    error: Error,
    _translateFn: (key: string, defaultValue?: string) => string,
    context?: Record<string, unknown>,
  ): ProcessedError {
    return {
      message: error.message,
      code: 'GENERIC_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, originalMessage: error.message, stack: error.stack },
    }
  }

  /**
   * Process string errors with translation support
   * Pass-through: see processGenericErrorWithTranslation rationale.
   */
  private processStringErrorWithTranslation(
    error: string,
    _translateFn: (key: string, defaultValue?: string) => string,
    context?: Record<string, unknown>,
  ): ProcessedError {
    return {
      message: error,
      code: 'STRING_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, originalMessage: error },
    }
  }

  /**
   * Process unknown error types with translation support
   */
  private processUnknownErrorWithTranslation(
    error: unknown,
    translateFn: (key: string, defaultValue?: string) => string,
    context?: Record<string, unknown>,
  ): ProcessedError {
    return {
      message: this.getLocalizedMessage(
        'UNKNOWN_ERROR',
        'An unexpected error occurred',
        translateFn,
      ),
      code: 'UNKNOWN_ERROR',
      severity: 'error',
      shouldNotify: true,
      shouldLog: true,
      context: { ...context, error: String(error) },
    }
  }

  /**
   * Handle error with automatic user feedback
   */
  handleError(error: unknown, context?: Record<string, unknown>): ProcessedError {
    const processedError = this.processError(error, context)

    // Log error if needed
    if (processedError.shouldLog) {
      logger.error('Processed Error:', processedError)
    }

    // Show notification if needed
    if (processedError.shouldNotify && this.notificationHook) {
      if (processedError.severity === 'error') {
        this.notificationHook.error(processedError.message)
      } else if (processedError.severity === 'warning') {
        this.notificationHook.warning(processedError.message)
      } else {
        this.notificationHook.info(processedError.message)
      }
    }

    return processedError
  }

  /**
   * Handle success with automatic user feedback
   */
  handleSuccess(message: string, context?: Record<string, unknown>) {
    if (this.notificationHook) {
      this.notificationHook.success(message)
    }

    logger.info('Success:', message, context)
  }

  /**
   * Get severity from HTTP status code
   */
  private getSeverityFromStatus(status?: number): 'error' | 'warning' | 'info' {
    if (!status) return 'error'
    if (status >= 500) return 'error'
    if (status >= 400) return 'error'
    if (status >= 300) return 'warning'
    return 'info'
  }

  /**
   * Get message directly from backend - NO INTERPRETATION
   */
  private getLocalizedMessage(
    code: string,
    defaultMessage: string,
    _translateFn?: (key: string, defaultValue?: string) => string,
  ): string {
    // ALWAYS use the defaultMessage from backend directly
    // NO code mapping, NO interpretation, NO translation in frontend
    return defaultMessage
  }
}

// Singleton instance
export const errorService = ErrorService.getInstance()
